/**
 * AEGIS-11 risk intelligence seed.
 *
 * Seeds aegis_risk_events with 7 realistic events spanning the risk
 * categories the spec calls out (tariff, port disruption, geopolitical,
 * trade restriction, regulatory) plus per-country risk scores for
 * 25 countries relevant to the demo tenant's freight footprint.
 *
 * Event records are designed so the RiskDashboard has immediate signal
 * on first load — active events with affected entities computed live
 * by the client service.
 */
import { DEMO_TENANT_ID } from './tenantDefaults';

const now = new Date();
const daysAhead = (d) => new Date(now.getTime() + d * 86400000).toISOString();
const daysAgo = (d) => new Date(now.getTime() - d * 86400000).toISOString();

const base = (extra = {}) => ({
  tenantId: DEMO_TENANT_ID,
  createdAt: daysAgo(10),
  createdBy: 'aegis_auto',
  updatedAt: daysAgo(1),
  updatedBy: 'aegis_auto',
  isDeleted: false,
  softDeleted: false,
  version: 1,
  source: 'risk_seed',
  ...extra,
});

// ── Risk events ────────────────────────────────────────────────────────
export const SEED_AEGIS_RISK_EVENTS = [
  // 1. Active tariff — Section 301 increase on Chinese electronics
  {
    id: 'rsk-001', eventId: 'rsk-001',
    kind: 'tariff',
    title: 'Section 301 tariff increase on Chinese electronics (HS 8541, 8542, 8544)',
    summary: 'USTR announced an incremental 15% duty on listed semiconductor and wiring harness imports from China, effective in 14 days.',
    severity: 'high',
    status: 'active',
    source: 'USTR Federal Register',
    sourceUrl: 'https://ustr.gov/example',
    publishedAt: daysAgo(3),
    effectiveDate: daysAhead(11),
    expiresAt: null,
    affectedCountries: ['CN'],
    affectedPorts: [],
    affectedCommodities: ['electronics', 'semiconductors', 'wiring harness'],
    htsCodes: ['8541', '8542', '8544'],
    tariffRateIncrease: 0.15,   // 15% increase on assessed value
    estimatedImpactUsd: 148000,
    confidence: 0.9,
    tags: ['section_301', 'china', 'semiconductors'],
    mitigationOptions: [
      { id: 'accelerate', label: 'Accelerate shipments before effective date', impactUsd: -148000, effort: 'medium' },
      { id: 'reroute', label: 'Reroute sourcing via Vietnam / Malaysia', impactUsd: -95000, effort: 'high' },
      { id: 'ftz', label: 'Hold inventory in FTZ and release post-expiration', impactUsd: -60000, effort: 'medium' },
    ],
    aegisNotes: 'Customer impact concentrated on two accounts with China-sourced electronics. Acceleration window closes in 11 days.',
    ...base({ createdAt: daysAgo(3), updatedAt: daysAgo(1) }),
  },

  // 2. Active port disruption — Panama Canal water levels
  {
    id: 'rsk-002', eventId: 'rsk-002',
    kind: 'port_disruption',
    title: 'Panama Canal water-level restrictions — slot reductions into Q2',
    summary: 'ACP has cut daily transit slots by 24% and reduced maximum draft by 3 feet. Expected to persist through June.',
    severity: 'high',
    status: 'active',
    source: 'Panama Canal Authority',
    sourceUrl: 'https://pancanal.com/example',
    publishedAt: daysAgo(18),
    effectiveDate: daysAgo(14),
    expiresAt: daysAhead(60),
    affectedCountries: [],
    affectedPorts: ['PANAMA_CANAL', 'BALBOA', 'COLON'],
    affectedCommodities: [],
    htsCodes: [],
    tariffRateIncrease: 0,
    estimatedImpactUsd: 62000,   // demurrage + longer transit on affected loads
    confidence: 0.85,
    tags: ['panama_canal', 'ocean', 'transit_delay'],
    mitigationOptions: [
      { id: 'suez', label: 'Re-route Asia-East Coast via Suez', impactUsd: 22000, effort: 'high' },
      { id: 'intermodal', label: 'LA/LB discharge + intermodal to East Coast', impactUsd: -18000, effort: 'medium' },
      { id: 'buffer', label: 'Build 10-day buffer into delivery commitments', impactUsd: 0, effort: 'low' },
    ],
    aegisNotes: 'All open ocean loads routing Panama flagged; customer visibility view updated.',
    ...base({ createdAt: daysAgo(18), updatedAt: daysAgo(2) }),
  },

  // 3. Geopolitical — Red Sea shipping disruption
  {
    id: 'rsk-003', eventId: 'rsk-003',
    kind: 'geopolitical',
    title: 'Red Sea — Bab el-Mandeb strait shipping diversions',
    summary: 'Security advisory in effect; major carriers continue to route around Cape of Good Hope, adding 10-14 days and 20-30% fuel surcharge.',
    severity: 'critical',
    status: 'active',
    source: 'UKMTO advisory',
    sourceUrl: 'https://ukmto.org/example',
    publishedAt: daysAgo(60),
    effectiveDate: daysAgo(55),
    expiresAt: null,
    affectedCountries: ['EG', 'YE'],
    affectedPorts: ['SUEZ', 'DJIBOUTI', 'ADEN'],
    affectedCommodities: [],
    htsCodes: [],
    tariffRateIncrease: 0,
    estimatedImpactUsd: 38000,
    confidence: 0.95,
    tags: ['red_sea', 'geopolitical', 'ocean', 'fuel_surcharge'],
    mitigationOptions: [
      { id: 'cape_route', label: 'Accept Cape of Good Hope routing on affected lanes', impactUsd: 38000, effort: 'low' },
      { id: 'air_bridge', label: 'Air-freight urgent shipments selectively', impactUsd: 48000, effort: 'high' },
      { id: 'customer_comm', label: 'Proactive customer notification on affected transit times', impactUsd: 0, effort: 'low' },
    ],
    aegisNotes: 'Ongoing situation — AEGIS auto-notifies affected customers weekly.',
    ...base({ createdAt: daysAgo(60), updatedAt: daysAgo(3) }),
  },

  // 4. Active tariff — EU Steel + Aluminum safeguard
  {
    id: 'rsk-004', eventId: 'rsk-004',
    kind: 'tariff',
    title: 'EU safeguard extension on steel + aluminum (HS 7208, 7219, 7606)',
    summary: 'EU Commission extended safeguard duties through next June; quota-based additional tariff applies above country-specific caps.',
    severity: 'medium',
    status: 'active',
    source: 'EU Commission',
    sourceUrl: 'https://ec.europa.eu/example',
    publishedAt: daysAgo(28),
    effectiveDate: daysAgo(20),
    expiresAt: daysAhead(240),
    affectedCountries: ['CN', 'IN', 'TR', 'VN'],
    affectedPorts: [],
    affectedCommodities: ['steel', 'aluminum'],
    htsCodes: ['7208', '7219', '7606'],
    tariffRateIncrease: 0.25,
    estimatedImpactUsd: 18000,
    confidence: 0.8,
    tags: ['eu', 'safeguard', 'steel', 'aluminum'],
    mitigationOptions: [
      { id: 'us_source', label: 'Shift EU-bound orders to US or Canadian mills', impactUsd: -12000, effort: 'high' },
      { id: 'quota_timing', label: 'Time shipments to country quota windows', impactUsd: -8000, effort: 'medium' },
    ],
    aegisNotes: 'One customer (Century Aluminum) has open POs affected — see detail.',
    ...base({ createdAt: daysAgo(28), updatedAt: daysAgo(5) }),
  },

  // 5. Active — Mexico border commercial crossing delays
  {
    id: 'rsk-005', eventId: 'rsk-005',
    kind: 'port_disruption',
    title: 'Mexico border: Laredo + Otay Mesa commercial inspection delays',
    summary: 'Increased CBP commercial inspection staffing shifts are extending crossing times 4-8 hours at Laredo and Otay Mesa.',
    severity: 'medium',
    status: 'active',
    source: 'CBP operational advisory',
    sourceUrl: 'https://cbp.gov/example',
    publishedAt: daysAgo(6),
    effectiveDate: daysAgo(5),
    expiresAt: daysAhead(14),
    affectedCountries: ['MX'],
    affectedPorts: ['LAREDO', 'OTAY_MESA'],
    affectedCommodities: [],
    htsCodes: [],
    tariffRateIncrease: 0,
    estimatedImpactUsd: 9200,
    confidence: 0.75,
    tags: ['mexico', 'border', 'crossing_delay'],
    mitigationOptions: [
      { id: 'el_paso', label: 'Route via El Paso / Ysleta', impactUsd: 2200, effort: 'low' },
      { id: 'night_cross', label: 'Schedule night crossings to avoid peak', impactUsd: 0, effort: 'low' },
    ],
    aegisNotes: 'Short-duration event; AEGIS recommends timing workaround over reroute.',
    ...base({ createdAt: daysAgo(6), updatedAt: daysAgo(1) }),
  },

  // 6. Upcoming — USMCA steel/aluminum monitoring tightens
  {
    id: 'rsk-006', eventId: 'rsk-006',
    kind: 'trade_restriction',
    title: 'USMCA melt-and-pour origin verification tightens Q2',
    summary: 'Stricter documentation for country-of-melt origin on steel tariff-rate quotas takes effect next quarter.',
    severity: 'low',
    status: 'upcoming',
    source: 'USMCA Secretariat',
    sourceUrl: 'https://can-mex-usa-sec.org/example',
    publishedAt: daysAgo(14),
    effectiveDate: daysAhead(45),
    expiresAt: null,
    affectedCountries: ['CN', 'RU'],
    affectedPorts: [],
    affectedCommodities: ['steel'],
    htsCodes: ['7208', '7210'],
    tariffRateIncrease: 0,
    estimatedImpactUsd: 2200,
    confidence: 0.7,
    tags: ['usmca', 'origin_verification', 'documentation'],
    mitigationOptions: [
      { id: 'mill_cert', label: 'Collect mill certificates at PO stage', impactUsd: 0, effort: 'low' },
    ],
    aegisNotes: 'Informational at current thresholds.',
    ...base({ createdAt: daysAgo(14), updatedAt: daysAgo(5) }),
  },

  // 7. Resolved — Baltimore port reopening
  {
    id: 'rsk-007', eventId: 'rsk-007',
    kind: 'port_disruption',
    title: 'Port of Baltimore — channel reopened after debris clearance',
    summary: 'USCG reopened the main channel with normal draft restored. Historical record; residual backlog clearing over 2 weeks.',
    severity: 'low',
    status: 'resolved',
    source: 'USCG Marine Safety Information Bulletin',
    sourceUrl: 'https://uscg.mil/example',
    publishedAt: daysAgo(45),
    effectiveDate: daysAgo(90),
    expiresAt: daysAgo(30),
    affectedCountries: [],
    affectedPorts: ['BALTIMORE'],
    affectedCommodities: [],
    htsCodes: [],
    tariffRateIncrease: 0,
    estimatedImpactUsd: 0,
    confidence: 1.0,
    tags: ['baltimore', 'port', 'resolved'],
    mitigationOptions: [],
    aegisNotes: 'Archived. No ongoing impact.',
    ...base({ createdAt: daysAgo(90), updatedAt: daysAgo(30) }),
  },
];

// ── Country risk scores (0-100, higher = more risk) ───────────────────
// Component scores: political, economic, operational, trade restrictions.
// Aggregate score is the max component weighted 40/20/20/20.
function country(code, name, region, political, economic, operational, trade, tags = []) {
  const aggregate = Math.round(political * 0.4 + economic * 0.2 + operational * 0.2 + trade * 0.2);
  return {
    id: `crk-${code.toLowerCase()}`,
    countryCode: code,
    countryName: name,
    region,
    aggregateScore: aggregate,
    political, economic, operational, trade,
    trend: political > 60 ? 'worsening' : 'stable',
    tags,
    lastUpdatedAt: daysAgo(2),
    ...base({ createdAt: daysAgo(30), updatedAt: daysAgo(2) }),
  };
}

export const SEED_AEGIS_COUNTRY_RISK = [
  // Higher-risk
  country('RU', 'Russia',       'Europe',    95, 85, 70, 95, ['sanctions']),
  country('YE', 'Yemen',        'Middle East', 95, 90, 90, 60, ['active_conflict']),
  country('IR', 'Iran',         'Middle East', 90, 80, 60, 95, ['sanctions']),
  country('VE', 'Venezuela',    'South America', 80, 90, 70, 70, ['sanctions']),
  country('CN', 'China',        'Asia',      60, 45, 30, 75, ['section_301']),
  country('EG', 'Egypt',        'Africa',    55, 60, 55, 35, ['red_sea_exposure']),
  country('TR', 'Turkey',       'Europe',    50, 55, 35, 55, []),
  country('IN', 'India',        'Asia',      40, 40, 45, 35, []),
  country('MX', 'Mexico',       'N. America', 45, 40, 45, 30, ['border_delays']),
  // Medium
  country('BR', 'Brazil',       'S. America', 40, 45, 40, 25, []),
  country('VN', 'Vietnam',      'Asia',      30, 30, 35, 30, []),
  country('ID', 'Indonesia',    'Asia',      35, 35, 40, 30, []),
  country('ZA', 'South Africa', 'Africa',    45, 50, 50, 25, []),
  country('TH', 'Thailand',     'Asia',      30, 35, 35, 20, []),
  country('PH', 'Philippines',  'Asia',      35, 35, 40, 20, []),
  country('CO', 'Colombia',     'S. America', 35, 40, 45, 20, []),
  // Lower
  country('DE', 'Germany',      'Europe',    15, 20, 10, 20, []),
  country('GB', 'United Kingdom', 'Europe',  15, 20, 10, 15, []),
  country('FR', 'France',       'Europe',    18, 20, 15, 20, []),
  country('NL', 'Netherlands',  'Europe',    10, 15, 10, 15, []),
  country('JP', 'Japan',        'Asia',      10, 15, 10, 15, []),
  country('KR', 'South Korea',  'Asia',      20, 20, 10, 20, []),
  country('TW', 'Taiwan',       'Asia',      45, 25, 15, 30, ['strait_tensions']),
  country('CA', 'Canada',       'N. America', 10, 15, 10, 10, []),
  country('US', 'United States', 'N. America', 15, 15, 10, 10, []),
];

export const RISK_SEED_SUMMARY = {
  aegis_risk_events: SEED_AEGIS_RISK_EVENTS.length,
  aegis_country_risk: SEED_AEGIS_COUNTRY_RISK.length,
};
